"use client";

import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { useTranslation } from "react-i18next";
import { api, type MarketDetail } from "@/lib/api";
import { formatVolumeCents } from "@/lib/format";
import { MarketMidpointDisplay } from "@/components/MarketMidpointDisplay";

interface LiveMarketStatsProps {
  // The full market record from the page's initial server render.
  // Used as the seed; live updates replace the relevant fields on
  // market:trade events.
  initial: MarketDetail;
}

// Trade-event payload shape from MarketsGateway.emitTrade. We only
// need the slug filter — the page refetches the full market record
// to avoid drift from partial-event state patching.
interface IncomingTradeEvent {
  marketSlug: string;
}

/**
 * Live wrapper for the market detail page's percentage + stats
 * block. Server renders the initial values; this component
 * subscribes to market:trade events and refetches the market
 * record when trades land.
 *
 * Why refetch the full market instead of patching from the trade
 * payload: the trade event carries price + size + outcome but not
 * the aggregated volume / unique-traders / trade-count counters
 * (those live on the markets row and are bumped by triggers).
 * Refetch keeps the UI honest with DB state — same approach #213
 * uses for /home positions.
 *
 * Debounced ~600ms so a burst of fills doesn't fan out into a
 * burst of refetches. Burst case = matcher fills 3-5 makers in a
 * single placement; one refetch per burst is enough.
 */
const STATS_POLL_INTERVAL_MS = 10_000;

export function LiveMarketStats({ initial }: LiveMarketStatsProps) {
  const { t } = useTranslation();
  const [market, setMarket] = useState<MarketDetail>(initial);
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    if (!apiBase) return;

    function scheduleRefetch() {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      refetchTimerRef.current = setTimeout(() => {
        void api.getMarket(initial.slug).then((next) => setMarket(next));
      }, 600);
    }

    // 2026-05-09 — polling fallback for the stats grid. Same WS-down
    // posture as MarketMidpointDisplay: when the cloudflared tunnel
    // can't upgrade WS we still want volume / trade-count / liquidity
    // to refresh. Skips when the tab is in the background so we don't
    // burn data + API budget on idle viewers.
    pollIntervalRef.current = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      void api.getMarket(initial.slug).then((next) => setMarket(next));
    }, STATS_POLL_INTERVAL_MS);

    const url = apiBase.replace(/\/$/, "");
    let socket: Socket | null = null;
    try {
      socket = io(url, {
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 3,
        timeout: 8000,
      });
      socket.on("connect", () => {
        socket?.emit("subscribe", { slug: initial.slug });
      });
      socket.on("market:trade", (event: IncomingTradeEvent) => {
        if (event.marketSlug !== initial.slug) return;
        scheduleRefetch();
      });
      socket.on("market:orderbook", (event: IncomingTradeEvent) => {
        if (event.marketSlug !== initial.slug) return;
        scheduleRefetch();
      });
      // Swallow connect errors — polling fallback above keeps the
      // grid live regardless of WS state.
      socket.on("connect_error", () => {});
    } catch {
      // Defensive — socket constructor itself shouldn't throw.
    }

    return () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (socket) {
        try {
          socket.emit("unsubscribe", { slug: initial.slug });
          socket.disconnect();
        } catch {
          // ignore — cleanup-time failures are harmless.
        }
      }
    };
  }, [initial.slug]);

  // EPIC-053 STORY-1 (2026-05-08) — the duplicated "Probability (YES) NN%"
  // big-number was deleted (OutcomeCard buttons carry chance now). The
  // MarketMidpointDisplay below is a SEPARATE signal — orderbook midpoint
  // + spread, dynamic per fetch — and was restored 2026-05-09 after
  // founder flagged its removal. It's not a probability gauge; it's the
  // live-quote answer to "what would I trade at right now", which the
  // OutcomeCard buttons (last-trade per-outcome) don't answer.
  //
  // The chip re-mounts on every market.id change but doesn't yet
  // resubscribe to its own WS — the parent's market:orderbook /
  // market:trade chain triggers a market refetch (which can re-key
  // children), and MarketMidpointDisplay's own useEffect refetches the
  // liquidity snapshot on slug change. Promoting to its own WS subscriber
  // for sub-second drift is filed as STORY-692-display follow-up.
  return (
    <div className="flex flex-col gap-3">
      <MarketMidpointDisplay
        slug={initial.slug}
        // YES outcome's last-trade price (Polymarket fallback when
        // bid/ask spread > 10¢). sortOrder 0 = YES on binary markets;
        // null when no trades have landed yet (fresh market).
        lastTradePriceCents={
          market.outcomes.find((o) => o.sortOrder === 0)?.currentPriceCents ??
          null
        }
      />
      <div
        className="grid grid-cols-2 gap-3 rounded-md border border-[var(--color-border)] p-3 sm:grid-cols-4"
        style={{ background: "var(--color-surface)" }}
      >
        <LiveStat
          label={t("liveMarketStats.volume")}
          value={formatVolumeCents(market.totalVolumeCents)}
          tooltip={t("liveMarketStats.volumeTooltip")}
        />
        <LiveStat
          label={t("liveMarketStats.liquidity")}
          value={formatVolumeCents(market.totalLiquidityCents)}
          tooltip={t("liveMarketStats.liquidityTooltip")}
        />
        <LiveStat
          label={t("liveMarketStats.trades")}
          value={String(market.tradeCount)}
        />
        <LiveStat
          label={t("liveMarketStats.traders")}
          value={String(market.uniqueTraders)}
        />
      </div>
    </div>
  );
}

function LiveStat({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  tooltip?: string;
}) {
  // Vercel-redesign 2026-04-30 — left-aligned (was centered) with
  // smaller numerals + lighter weight. Stat cards on Vercel
  // dashboard use a label-on-top, value-below grid; values are
  // semibold mono.
  return (
    <div title={tooltip}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.6px] text-[var(--color-text3)]">
        {label}
      </div>
      <div className="mt-[2px] font-mono text-[14px] font-semibold tabular-nums text-[var(--color-text)]">
        {value}
      </div>
    </div>
  );
}
